import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { useApiClient } from '@/utils/api/ApiClient'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { TradingAccountsApiClient } from '@/utils/api/tradingAccountsApi'
import {
  GridColumn as Column,
  Grid,
  GridHeaderSelectionChangeEvent,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { AccountGroupDto } from 'api/Trading-Accounts/Types/AccountGroupDto'
import { FlexBox } from 'components/Box'
import { FilterType } from 'components/Filter'
import { getAccountGroupFilterFields } from 'components/Filter/accountGroupFields'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridStatusState } from 'features/DataGrid'
import uniqBy from 'lodash/uniqBy'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  setGridFilters,
} from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import { fetchCountriesAction } from 'store/identity/identity.actions'
import {
  fetchAllServerAction,
  fetchBooksTypeAction,
  fetchTradingAccountGroupTypeAction,
  fetchTradingAccountStatusAction,
  fetchTradingAccountTypeAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery } from 'utils/QueryHelpers'

import accountGroupColumn from './accountGroupColumn'
import { normalizeAccountGroupsSearchQuery } from './normalizer'

const useMapStateToProps = () => ({
  gridState: useAppSelector((state) => state.grid),
  servers: useAppSelector((state) => state.tradingAccounts.allServers),
  bookType: useAppSelector((state) => state.tradingAccounts.bookType),
  tradingAccountStatus: useAppSelector((state) => state.tradingAccounts.tradingAccountStatus),
  tradingAccountType: useAppSelector((state) => state.tradingAccounts.tradingAccountType),
  tradingAccountGroupType: useAppSelector((state) => state.tradingAccounts.tradingAccountGroupType),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  return {
    fetchServers: () => dispatch(fetchAllServerAction(tradingAccountsApiClient)),
    fetchBookTypes: () => dispatch(fetchBooksTypeAction(tradingAccountsApiClient)),
    fetchTradingAccountStatus: () =>
      dispatch(fetchTradingAccountStatusAction(tradingAccountsApiClient)),
    fetchCountries: () => dispatch(fetchCountriesAction(identityApiClient)),
    fetchTradingAccountType: () =>
      dispatch(fetchTradingAccountTypeAction(tradingAccountsApiClient)),
    fetchTradingAccountGroupType: () =>
      dispatch(fetchTradingAccountGroupTypeAction(tradingAccountsApiClient)),
    setGridFilters: (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: () => dispatch(resetGridFilters()),
  }
}

interface Props {
  selectedAccountGroups: AccountGroupDto[]
  onClose: () => void
  onSelect: (accountGroupDtos: AccountGroupDto[]) => void
  introducingBrokerProfileId: string
}

const SelectAccountGroupTable: React.FC<Props> = (props) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const [accountGroups, setAccountGroups] = useState<AccountGroupDto[]>([])
  const [gridFilterState, setGridFilterState] = useState<GridFilterActionState>(
    getPaginationInitialState()
  )
  const [selectedRecords, setSelectedRecords] = useState<AccountGroupDto[]>([])
  const { servers, bookType, tradingAccountStatus, tradingAccountType, tradingAccountGroupType } =
    useMapStateToProps()
  const {
    fetchServers,
    fetchBookTypes,
    fetchTradingAccountStatus,
    fetchTradingAccountType,
    fetchTradingAccountGroupType,
  } = useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterActionState: GridFilterActionState
        ): Promise<PageData<AccountGroupDto[]>> => {
          const response = await tradingAccountsApiClient.getAvailableAccountGroups(
            props.introducingBrokerProfileId,
            {
              pageIndex: gridFilterActionState.pageIndex,
              pageSize: gridFilterActionState.pageSize,
            }
          )

          if (isEmpty(servers)) await fetchServers()
          if (isEmpty(bookType)) await fetchBookTypes()
          if (isEmpty(tradingAccountStatus)) await fetchTradingAccountStatus()
          if (isEmpty(tradingAccountType)) await fetchTradingAccountType()
          if (isEmpty(tradingAccountGroupType)) await fetchTradingAccountGroupType()

          setSelectedRecords(props.selectedAccountGroups)
          setAccountGroups(
            response.items.map((accountGroupDto: AccountGroupDto) => {
              if (props.selectedAccountGroups.length <= 0) {
                return { ...accountGroupDto, selected: false }
              }

              const match = props.selectedAccountGroups.find((x) => x.id === accountGroupDto.id)
              const isSelected = !!match

              return { ...accountGroupDto, selected: isSelected }
            })
          )

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<AccountGroupDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridState = createGridState(getPaginationInitialState(), gridFilterState)
    const response = await callback(gridState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      setGridFilterState({
        ...gridState,
        pageIndex: gridState.pageIndex,
        pageSize: gridState.take,
        skip: gridState.skip,
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const filterFetch = useCallback(
    async (params?: {
      query?: string | null
      filter?: CompositeFilterDescriptor
    }): Promise<void> => {
      const searchQuery =
        params?.query !== undefined ? params.query : getQuery(gridFilterState.filter)
      const pageIndex = 1
      const pageSize = gridFilterState.pageSize ?? 10
      const take = gridFilterState.take ?? 10
      const skip = 0

      const response = await tradingAccountsApiClient.getAvailableAccountGroups(
        props.introducingBrokerProfileId,
        {
          search: searchQuery,
          pageIndex,
          pageSize,
        }
      )
      const total = response.totalResults

      if (response?.items) {
        setAccountGroups(response.items.map((dataItem) => ({ ...dataItem, selected: false })))
      }
      setGridFilterState({
        ...gridFilterState,
        filter: params?.filter ?? gridFilterState.filter,
        take,
        skip,
        total,
        pageIndex,
        pageSize,
      })
    },
    [gridFilterState, props.introducingBrokerProfileId] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      const gridFilter = filter ?? gridFilterState.filter
      return buildQuery(normalizeAccountGroupsSearchQuery(gridFilter)) ?? null
    },
    [gridFilterState.filter]
  )

  const selectTradingAccounts = (tradingAccountItems: AccountGroupDto[]) =>
    tradingAccountItems.map((tradingAccount: AccountGroupDto) => {
      const match = selectedRecords.find((x) => x.id === tradingAccount.id)
      const isSelected = !!match
      return { ...tradingAccount, selected: isSelected }
    })

  const handleGridPageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip: event.page.skip,
      recordsPerPage: take,
    })
    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()
    const searchQuery = getQuery(gridFilterState.filter)

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
      pageSize,
    })

    const response = await tradingAccountsApiClient.getAvailableAccountGroups(
      props.introducingBrokerProfileId,
      {
        search: searchQuery,
        pageIndex,
        pageSize,
      }
    )
    const total = response.totalResults

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
      pageSize,
      total,
    })
    setAccountGroups(selectTradingAccounts(response.items))
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    setAccountGroups(
      accountGroups.map((tradingAccount) => ({ ...tradingAccount, selected: isChecked }))
    )

    const tradingAccountsSelected = isChecked
      ? uniqBy([...accountGroups, ...selectedRecords], 'id')
      : selectedRecords.filter((selectedTradingAccount) => {
          const hasAccountGroups = accountGroups.find((x) => x.id === selectedTradingAccount.id)

          return !hasAccountGroups
        })

    setSelectedRecords(tradingAccountsSelected)
  }

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    if (!event.dataItem.selected) {
      const accountGroup = accountGroups.find((x) => x.id === event.dataItem.id)
      if (accountGroup) {
        setSelectedRecords([...selectedRecords, accountGroup])
      }
    } else {
      setSelectedRecords(selectedRecords.filter((x) => x.id !== event.dataItem.id))
    }

    setAccountGroups(
      accountGroups.map((item) => {
        if (item.id === event.dataItem.id) {
          return { ...item, selected: !event.dataItem.selected }
        }
        return item
      })
    )
  }

  const handleSubmitTradingAccounts = async () => {
    await props.onSelect(uniqBy(selectedRecords, 'id'))
    props.onClose()
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <div className='trading-account-table' style={{ margin: 16, overflowY: 'scroll' }}>
      <SelectFiltersFacade
        gridState={gridFilterState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={(next) => setGridFilterState((prev) => ({ ...prev, ...next }))}
        filterFetch={filterFetch}
        filterType={FilterType.ACCOUNT_GROUP}
        filterFields={getAccountGroupFilterFields()}
        containerClassName='trading-account-table__filter'
        getQuery={getQuery}
        showFilterWhenInactive
      />
      <Grid
        {...gridFilterState}
        resizable
        data={accountGroups}
        selectedField='selected'
        sortable={{
          allowUnsort: true,
          mode: 'multiple',
        }}
        total={gridFilterState.total}
        take={gridFilterState.take}
        skip={gridFilterState.skip}
        onSelectionChange={handleSelectionChange}
        onHeaderSelectionChange={handleHeaderSelectionChange}
        onPageChange={handleGridPageChange}
        pageable={pageableSettings}
        style={{ zIndex: 0 }}
      >
        <Column
          field='selected'
          width='50px'
          headerSelectionValue={accountGroups.findIndex((dataItem) => !dataItem.selected) === -1}
        />
        {accountGroupColumn
          .filter((c) => c.show)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
      </Grid>
      <FlexBox style={{ margin: 32 }} alignItems='center' justifyContent='center'>
        <Button style={{ cursor: 'pointer' }} onClick={handleSubmitTradingAccounts}>
          Save
        </Button>
      </FlexBox>
    </div>
  )
}

export default SelectAccountGroupTable
